import { z } from 'zod';

const ProjectKey = z.string().regex(/^[A-Z][A-Z0-9]{1,9}$/, {
  message: 'Project key: 2-10 chars, UPPERCASE letters/digits, starts with letter (e.g. WEB)',
});
const ProjectSlug = z.string().regex(/^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/, {
  message: 'Slug: 1-40 chars, lowercase alphanumeric + dash',
});
const ProjectName = z.string().min(1).max(80);
const ColorHex = z.string().regex(/^#[0-9a-fA-F]{6}$/);
const Currency = z.string().regex(/^[A-Z]{3}$/);

const Visibility = z.enum(['team', 'restricted']);
const Status = z.enum(['active', 'on_hold', 'completed', 'archived']);
const AssigneeStrategy = z.enum(['creator', 'respondent', 'unassigned']);

/**
 * `projects.system_kind` (migration 0114). `'inbox'` = the capture project in
 * a personal team (key ME): never listed as a project — the UI shows it as
 * the "Personal" chip (`isNavigableProject`) — and it can't be archived,
 * deleted, renamed, moved or shared (409 `system_project_locked`).
 * null = an ordinary project.
 */
export const ProjectSystemKind = z.enum(['inbox']);
export type ProjectSystemKindValue = z.infer<typeof ProjectSystemKind>;

/**
 * `projects.external_url` (W6-E, migration 0122): an External project is a
 * Personal-team project that stands for work I do somewhere I'm not a member
 * (label = the project name, link = this URL). Only on projects of a personal
 * team (anything else → 400 `external_url_personal_only`); http(s) only.
 * Projections carry it as optional until W6-E lands (absent = null).
 */
export const ProjectExternalUrl = z
  .string()
  .trim()
  .url()
  .max(2000)
  .refine((u) => /^https?:\/\//i.test(u), { message: 'http(s) URL expected' });

/** Allowed values for `projects.default_view` — must stay in sync with
 *  the CHECK constraint in migration 0052 AND the URL-search enum in
 *  apps/web/src/app/routes/teams.$teamSlug.projects.$projectSlug.tsx. */
const DefaultView = z.enum([
  'overview',
  'board',
  'list',
  'timeline',
  'calendar',
  'docs',
  'reports',
]);
export type ProjectDefaultView = z.infer<typeof DefaultView>;

const TaskTypeItem = z.object({
  id: z.string().min(1).max(40),
  name: z.string().min(1).max(40),
  /** Lucide icon name or arbitrary short token; the frontend renders a
   *  fallback dot if it doesn't recognise the value. */
  icon: z.string().max(40).optional(),
  /** Optional — existing team-default task types (seeded before chat #5)
   *  don't carry colors; the UI falls back to a neutral chip. */
  color: ColorHex.optional(),
});
// ── create / update ──────────────────────────────────────────────────────────
export const CreateProjectSchema = z.object({
  name: ProjectName,
  /** Optional — if omitted, derived from name (uppercased prefix). */
  key: ProjectKey.optional(),
  /** Optional — if omitted, derived from name (lowercase, dashed). */
  slug: ProjectSlug.optional(),
  description: z.unknown().optional(),         // TipTap JSON
  icon: z.string().max(40).optional(),
  color: ColorHex.optional(),
  visibility: Visibility.default('team'),
  isBillable: z.boolean().default(false),
  timeTrackingRequired: z.boolean().default(false),
  defaultTaskAssigneeStrategy: AssigneeStrategy.default('unassigned'),
  /** IANA tz the project's deadlines run in.  Defaults to UTC server-side
   *  when omitted; UI suggests the user's own tz at creation time. */
  timezone: z.string().min(1).max(64).optional(),
  /** External project (W6-E): personal team only. */
  externalUrl: ProjectExternalUrl.optional(),
});
export type CreateProjectInput = z.infer<typeof CreateProjectSchema>;

export const UpdateProjectSchema = z
  .object({
    name: ProjectName.optional(),
    description: z.unknown().nullable().optional(),
    icon: z.string().max(40).nullable().optional(),
    color: ColorHex.nullable().optional(),
    coverImageUrl: z.string().url().nullable().optional(),
    visibility: Visibility.optional(),
    isBillable: z.boolean().optional(),
    timeTrackingRequired: z.boolean().optional(),
    defaultTaskAssigneeStrategy: AssigneeStrategy.optional(),
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    targetDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    status: Status.optional(),
    /**
     * Money (doc 18 WS-H): writing it — or `budgetHours` / `isBillable` /
     * `currency` — needs finance edit access in a browser session, else 403
     * `finance_edit_forbidden`. Capped at 1 000 000.00 per hour (the column is int4).
     */
    hourlyRateCents: z.number().int().nonnegative().max(100_000_000).nullable().optional(),
    budgetHours: z.number().nonnegative().nullable().optional(),
    currency: Currency.optional(),
    timezone: z.string().min(1).max(64).optional(),
    /** GitHub integration — repo full name `owner/repo`.  Empty string / null clears the link. */
    githubRepoFullName: z
      .string()
      .regex(/^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/, 'expected owner/repo')
      .max(128)
      .nullable()
      .optional(),
    /** GitHub installation id (BIGINT on the wire as a numeric string). */
    githubInstallationId: z
      .union([z.string().regex(/^\d+$/).max(20), z.number().int().nonnegative()])
      .nullable()
      .optional(),
    /** Project-wide default landing tab.  NULL clears the preference. */
    defaultView: DefaultView.nullable().optional(),
    /** External project link (W6-E; personal team only).  NULL clears it. */
    externalUrl: ProjectExternalUrl.nullable().optional(),
  })
  .strict();
export type UpdateProjectInput = z.infer<typeof UpdateProjectSchema>;

export const DeleteProjectSchema = z.object({
  confirmKey: ProjectKey,
});
export type DeleteProjectInput = z.infer<typeof DeleteProjectSchema>;

// ── settings (workflow override) ─────────────────────────────────────────────
/** An advertised status = a workflow state: id ≤ 80 (state key), name ≤ 120 (state label). */
const StatusItem = z.object({
  id: z.string().min(1).max(80),
  name: z.string().min(1).max(120),
  color: ColorHex,
  category: z.enum(['open', 'in_progress', 'review', 'done', 'cancelled']),
  order: z.number().int().nonnegative(),
});
const PriorityItem = z.object({
  id: z.string().min(1).max(40),
  name: z.string().min(1).max(40),
  level: z.number().int().min(1).max(4),
  color: ColorHex,
});

export const UpdateProjectWorkflowSchema = z
  .object({
    /** Deprecated — statuses come from the project's workflow; any value → 400 `deprecated`. */
    taskStatuses: z.array(StatusItem).nullable().optional(),
    priorities: z.array(PriorityItem).nullable().optional(),
    /** Per-project task-type catalog override.  Same NULL / array semantics
     *  as `taskStatuses`. */
    taskTypes: z.array(TaskTypeItem).nullable().optional(),
  })
  .strict();
export type UpdateProjectWorkflowInput = z.infer<typeof UpdateProjectWorkflowSchema>;

// ── access ──────────────────────────────────────────────────────────────────
export const GrantProjectAccessSchema = z.object({
  userId: z.string().uuid(),
});
export type GrantProjectAccessInput = z.infer<typeof GrantProjectAccessSchema>;

// ── projection ───────────────────────────────────────────────────────────────
export const ProjectSchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  key: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.unknown().nullable(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  coverImageUrl: z.string().nullable(),
  status: Status,
  visibility: Visibility,
  startDate: z.string().nullable(),
  targetDate: z.string().nullable(),
  completedAt: z.string().nullable(),
  isBillable: z.boolean(),
  timeTrackingRequired: z.boolean(),
  defaultTaskAssigneeStrategy: AssigneeStrategy,
  /**
   * Money (doc 18 §4.3 L1, WS-H): the rate only for a finance viewer
   * (`time.view_billing`, not OUTSTAFF/VIEWER-derived) in a browser session —
   * `null` for everyone else, every PAT / MCP / AI caller and impersonation.
   * `isBillable`, `currency` and `budgetHours` (an hours budget) stay public.
   */
  hourlyRateCents: z.number().int().nullable(),
  budgetHours: z.number().nullable(),
  currency: z.string(),
  timezone: z.string().default('UTC'),
  taskCounter: z.number().int().nonnegative(),
  /** Resolved task statuses — project override OR team default if null. */
  resolvedTaskStatuses: z.array(StatusItem),
  resolvedPriorities: z.array(PriorityItem),
  resolvedTaskTypes: z.array(TaskTypeItem),
  /** Whether the project ROW carries an explicit override (not the team default). */
  hasOwnTaskStatuses: z.boolean(),
  hasOwnPriorities: z.boolean(),
  hasOwnTaskTypes: z.boolean(),
  /** Owner-set landing view; NULL means "no project preference". */
  defaultView: DefaultView.nullable(),
  /** GitHub integration — `owner/repo` once wired; null when not linked. */
  githubRepoFullName: z.string().nullable(),
  /** GitHub installation id serialized as numeric string (BIGINT). */
  githubInstallationId: z.string().nullable(),
  /** 'inbox' for the Personal capture project; null otherwise. */
  systemKind: ProjectSystemKind.nullable().default(null),
  /** External project link (W6-E); null / absent = an ordinary project. */
  externalUrl: z.string().nullable().optional(),
  createdAt: z.string(),
  archivedAt: z.string().nullable(),
});
export type ProjectProjection = z.infer<typeof ProjectSchema>;

export const ProjectSummarySchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  key: z.string(),
  slug: z.string(),
  name: z.string(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  status: Status,
  visibility: Visibility,
  isBillable: z.boolean(),
  timezone: z.string().default('UTC'),
  archivedAt: z.string().nullable(),
  /** 'inbox' for the Personal capture project (hide it from project lists); null otherwise. */
  systemKind: ProjectSystemKind.nullable().default(null),
  /** External project link (W6-E); null / absent = an ordinary project. */
  externalUrl: z.string().nullable().optional(),
  /** Resolved workflow shipped with the summary so consumers like the
   *  CreateTaskModal don't have to round-trip the full project just to
   *  know which statusId / priorityId / typeId to default to.  These honour
   *  any per-project override (`projects.taskStatuses ?? team.defaultTaskStatuses`). */
  resolvedTaskStatuses: z.array(StatusItem),
  resolvedPriorities: z.array(PriorityItem),
  resolvedTaskTypes: z.array(TaskTypeItem),
});
export type ProjectSummaryProjection = z.infer<typeof ProjectSummarySchema>;

/**
 * Active-task counts grouped by `statusId`, per project — feeds the status
 * donut on project cards.  Counts EXCLUDE archived + soft-deleted tasks.  The
 * client maps each `statusId` → category (`open|in_progress|review|done|
 * cancelled`) using the project's `resolvedTaskStatuses` (already in the
 * summary), so the server stays a trivial GROUP BY with no JSON resolution.
 */
export const ProjectTaskCountSchema = z.object({
  projectId: z.string().uuid(),
  statusId: z.string(),
  count: z.number().int().nonnegative(),
});
export type ProjectTaskCount = z.infer<typeof ProjectTaskCountSchema>;

export const ProjectAccessSchema = z.object({
  userId: z.string().uuid(),
  username: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  hasAccess: z.boolean(),
  grantedBy: z.string().uuid(),
  grantedAt: z.string(),
});
export type ProjectAccessProjection = z.infer<typeof ProjectAccessSchema>;

// ── Project Insights (Phase 1 feature #8 — heuristic, NO LLM) ──────────────

export const InsightKind = z.enum(['positive', 'warning', 'risk', 'info']);
export type InsightKindValue = z.infer<typeof InsightKind>;

export const InsightSchema = z.object({
  kind: InsightKind,
  text: z.string(),
});
export type InsightProjection = z.infer<typeof InsightSchema>;

export const ProjectInsightsResponseSchema = z.object({
  insights: z.array(InsightSchema),
  generatedAt: z.string(),
});
export type ProjectInsightsProjection = z.infer<typeof ProjectInsightsResponseSchema>;
